import React, { useState, useEffect } from "react";
import { Modal, Button, Badge } from "./UI";
import { apiFetch } from "../../context/AppContext";
import { Clock, User } from "lucide-react";

export default function QuestionActivityLogModal({ open, onClose, questionId, questionText }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && questionId) {
      fetchLogs();
    }
  }, [open, questionId]);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const res = await apiFetch(`/tenant/form-questions/activity-logs?question_id=${questionId}`);
      if (res.ok) {
        const data = await res.json();
        setLogs(data);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const getActionBadge = (action, details) => {
    switch (action) {
      case "create":
        return <Badge variant="success">Created</Badge>;
      case "update":
        return <Badge variant="info">Updated</Badge>;
      case "toggle_status":
        const newStatus = details?.changes?.is_active?.new;
        if (newStatus === true) {
          return <Badge variant="success">Activated</Badge>;
        } else if (newStatus === false) {
          return <Badge variant="danger">Deactivated</Badge>;
        }
        return <Badge variant="warning">Status Toggled</Badge>;
      case "bulk_upload":
        return <Badge variant="success">Bulk Uploaded</Badge>;
      case "import_defaults":
        return <Badge variant="success">Imported Default</Badge>;
      default:
        return <Badge variant="default">{action}</Badge>;
    }
  };

  const formatChanges = (changes) => {
    if (!changes || Object.keys(changes).length === 0) return null;
    return (
      <div className="mt-2 pl-4 border-l-2 border-slate-100 space-y-1 text-xs text-slate-600">
        {Object.entries(changes).map(([field, delta]) => {
          let oldVal = typeof delta.old === "boolean" ? (delta.old ? "Yes" : "No") : String(delta.old ?? "None");
          let newVal = typeof delta.new === "boolean" ? (delta.new ? "Yes" : "No") : String(delta.new ?? "None");
          if (field === "is_active") {
            oldVal = delta.old ? "Active" : "Inactive";
            newVal = delta.new ? "Active" : "Inactive";
          }
          return (
            <div key={field}>
              <span className="font-semibold capitalize">{field.replace("_", " ")}: </span>
              <span className="line-through text-red-500 bg-red-50 px-1 rounded">{oldVal}</span>
              <span className="mx-1">→</span>
              <span className="text-green-600 bg-green-50 px-1 rounded font-medium">{newVal}</span>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <Modal open={open} onClose={onClose} title="Question Activity History" size="md">
      <div className="space-y-4">
        {questionText && (
          <div className="bg-slate-50 border border-slate-100 rounded-xl p-3.5">
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Question Text</span>
            <p className="mt-1 text-sm font-semibold text-slate-800 line-clamp-3">{questionText}</p>
          </div>
        )}

        {loading ? (
          <div className="py-12 text-center text-sm text-slate-400">Loading history logs...</div>
        ) : logs.length === 0 ? (
          <div className="py-12 text-center text-sm text-slate-400">No activity history logs found for this question.</div>
        ) : (
          <div className="max-h-[350px] overflow-y-auto pr-2 pl-1 space-y-0">
            {logs.map((log, logIdx) => (
              <div key={log.id} className="relative flex gap-4 pb-6 last:pb-2">
                {logIdx !== logs.length - 1 && (
                  <span
                    className="absolute left-[13px] top-6 w-[2px] bg-slate-100"
                    style={{ height: "calc(100% - 12px)" }}
                    aria-hidden="true"
                  />
                )}
                <div className="relative flex-shrink-0 z-10 w-7 h-7 rounded-full bg-slate-50 border border-slate-200 flex items-center justify-center text-slate-500 shadow-sm">
                  <User size={13} />
                </div>
                <div className="flex-1 min-w-0 bg-white border border-slate-100 rounded-xl p-3 shadow-[0_1px_2px_rgba(0,0,0,0.02)] hover:border-slate-200 transition-colors">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <span className="text-xs font-bold text-slate-800">{log.admin_name || log.admin_email}</span>
                    <span className="text-[10px] text-slate-400 font-medium flex items-center gap-1">
                      <Clock size={10} />
                      {new Date(log.created_at).toLocaleString()}
                    </span>
                  </div>
                  <div className="mt-1.5 flex items-center gap-2">
                    {getActionBadge(log.action, log.details)}
                  </div>
                  {(log.action === "update" || log.action === "toggle_status") && log.details?.changes && formatChanges(log.details.changes)}
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="flex justify-end pt-2">
          <Button onClick={onClose} variant="secondary">Close</Button>
        </div>
      </div>
    </Modal>
  );
}
